import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import BibleProgress from './BibleProgress';

function Dashboard() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [biblePassage, setBiblePassage] = useState(null);
  const [passageLoading, setPassageLoading] = useState(true);
  const [passageError, setPassageError] = useState(null);
  const [userApps, setUserApps] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);

  // Check if user has app_editor role
  const isAppEditor = currentUser?.roles?.some(role => 
    role.name === 'app_editor' || role.name === 'admin' || role.name === 'superadmin'
  );

  // Function to get a random Bible book and chapter
  const getRandomPassage = () => {
    // List of popular Bible books and chapters for random selection
    const popularPassages = [
      { book: 'John', chapter: '3' },
      { book: 'Psalms', chapter: '23' },
      { book: 'Romans', chapter: '8' },
      { book: 'Matthew', chapter: '5' },
      { book: 'Philippians', chapter: '4' },
      { book: 'Proverbs', chapter: '3' },
      { book: 'Isaiah', chapter: '40' },
      { book: '1 Corinthians', chapter: '13' },
      { book: 'Genesis', chapter: '1' },
      { book: 'Hebrews', chapter: '11' }
    ];
    
    // Select a random passage
    const randomIndex = Math.floor(Math.random() * popularPassages.length);
    return popularPassages[randomIndex];
  };

  // Fetch Bible passage
  const fetchBiblePassage = async () => {
    try {
      setPassageLoading(true);
      setPassageError(null);
      
      const { book, chapter } = getRandomPassage();
      const response = await apiService.get(`/bible/passage/${book}/${chapter}`);
      setBiblePassage(response);
      setPassageLoading(false);
    } catch (err) {
      console.error('Failed to fetch Bible passage:', err);
      setPassageError('Failed to load Bible passage. Please try again later.');
      setPassageLoading(false);
    }
  };

  // Fetch user's apps if they are an app editor
  const fetchUserApps = async () => {
    if (!isAppEditor || !currentUser || !currentUser.id) return;
    
    try {
      setAppsLoading(true);
      // Use the correct endpoint with the current user's ID
      const response = await apiService.users.getApps(currentUser.id);
      setUserApps(response || []);
    } catch (err) {
      console.error('Failed to fetch user apps:', err);
    } finally {
      setAppsLoading(false);
    }
  };

  // Load Bible passage and user apps on component mount
  useEffect(() => {
    fetchBiblePassage();
    
    // Only fetch apps if user is editor and user ID is available
    if (isAppEditor && currentUser && currentUser.id) {
      fetchUserApps();
    }
  }, [isAppEditor, currentUser]);

  // Direct navigation to important sections
  const navigateToSection = (path) => {
    console.log(`Dashboard: Navigating to ${path}`);
    navigate(path);
  };

  if (passageLoading && !biblePassage) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading passage...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* App Editor Section - Only visible to app editors */}
      {isAppEditor && (
        <div className="app-editor-section mb-4">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">App Editor Tools</h5>
            </div>
            <div className="card-body">
              <h6>Manage Courses</h6>
              {appsLoading ? (
                <div className="text-center p-3">
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2 mb-0">Loading apps...</p>
                </div>
              ) : userApps.length > 0 ? (
                <div className="list-group">
                  {userApps.map(app => (
                    <Link 
                      key={app.id} 
                      to={`/apps/${app.id}/courses`} 
                      className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <strong>{app.name}</strong>
                        <p className="mb-0 text-muted small">{app.description}</p>
                      </div>
                      <span className="badge bg-primary rounded-pill">
                        <i className="fas fa-chevron-right"></i>
                      </span>
                    </Link>
                  ))}
                </div>
              ) : (
                <p>You don't have access to any apps. Contact an administrator to get access.</p>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Bible Progress Section */}
      <div className="bible-progress-section mb-4">
        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">Bible Reading Progress</h5>
          </div>
          <div className="card-body">
            <BibleProgress />
          </div>
        </div>
      </div>
      
      {/* Bible Passage Section */}
      <div className="bible-passage-section">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Chapter of the Day</h5>
            <div>
              {!passageLoading && !passageError && (
                <span className="text-muted me-3">{biblePassage?.book} {biblePassage?.chapter}</span>
              )}
              <button 
                className="btn btn-sm btn-outline-secondary" 
                onClick={fetchBiblePassage}
                disabled={passageLoading}
              >
                <i className="bi bi-arrow-clockwise"></i> Refresh
              </button>
            </div>
          </div>
          <div className="card-body">
            {passageLoading ? (
              <div className="text-center p-3">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 mb-0">Loading passage...</p>
              </div>
            ) : passageError ? (
              <div className="alert alert-danger" role="alert">
                {passageError}
              </div>
            ) : (
              <div>
                <div dangerouslySetInnerHTML={{ __html: biblePassage?.passageHtml }} />
                {biblePassage?.audioUrl && (
                  <div className="mt-3">
                    <audio controls className="w-100">
                      <source src={biblePassage.audioUrl} type="audio/mpeg" />
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard; 